import { useAssetStore } from '../../stores/assetStore';
import { useDragDrop } from '../../hooks/useDragDrop';
import { Plus, Image as ImageIcon } from '@phosphor-icons/react';

export default function AssetInspector() {
  const { selectedAssetId, assets } = useAssetStore();
  const { addAssetAtCenter } = useDragDrop();
  const asset = assets.find((a) => a.id === selectedAssetId);

  if (!asset) return null;

  const isImage = asset.spineVersion === 'image' ||
    (asset as unknown as Record<string, unknown>)['spine_version'] === 'image';
  const animations = asset.metadata?.animations ?? [];
  const skins = asset.metadata?.skins ?? [];

  return (
    <div className="overflow-y-auto">
      {/* Asset name */}
      <div className="px-4 py-3 border-b border-border-subtle">
        <p className="font-sans text-sm font-medium text-text-primary truncate">{asset.name}</p>
        {isImage ? (
          <div className="flex items-center gap-1 mt-0.5">
            <ImageIcon size={11} className="text-emerald-400 shrink-0" />
            <p className="font-mono text-[10.5px] text-emerald-400">Image</p>
          </div>
        ) : (
          <p className="font-mono text-[10.5px] text-text-tertiary mt-0.5">
            {animations.length} animation{animations.length !== 1 ? 's' : ''} · {skins.length} skin{skins.length !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Animations — spine assets only */}
      {!isImage && animations.length > 0 && (
        <>
          <div className="px-4 py-2 border-b border-border-subtle">
            <span className="font-serif text-sm font-medium text-text-secondary tracking-[0.01em]">Animations</span>
          </div>
          <div className="px-4 py-2 border-b border-border-subtle space-y-1">
            {animations.map((anim) => (
              <div key={anim} className="flex items-center gap-2 py-0.5">
                <div className="w-1.5 h-1.5 rounded-full bg-accent/50 shrink-0" />
                <span className="font-mono text-xs text-text-secondary truncate">{anim}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Skins — spine assets only */}
      {!isImage && skins.length > 1 && (
        <>
          <div className="px-4 py-2 border-b border-border-subtle">
            <span className="font-serif text-sm font-medium text-text-secondary tracking-[0.01em]">Skins</span>
          </div>
          <div className="px-4 py-2 border-b border-border-subtle space-y-1">
            {skins.map((skin) => (
              <div key={skin} className="flex items-center gap-2 py-0.5">
                <div className="w-1.5 h-1.5 rounded-full bg-violet/60 shrink-0" />
                <span className="font-mono text-xs text-text-secondary truncate">{skin}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Add to scene */}
      <div className="px-4 py-3">
        <button
          onClick={() => addAssetAtCenter(asset)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md bg-accent text-white text-xs font-sans font-medium hover:bg-accent-hover transition-all duration-normal"
        >
          <Plus size={13} weight="bold" />
          Add to Scene
        </button>
      </div>
    </div>
  );
}
